import React, { useEffect, useState, useCallback, useRef } from 'react';
import './Hero.css';

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [zipCodeResult, setZipCodeResult] = useState(null);
  const requestRef = useRef();
  const previousTimeRef = useRef();

  // Service area zip codes
  const serviceAreaZipCodes = [
    // Palm Coast
    '32135',
    '32137',
    '32142',
    '32164',
    '32110',
    '32136',
    // Flagler Beach
    '32136',
    // Ormond Beach
    '32174',
    '32176',
  ];

  const checkZipCode = useCallback((zip) => {
    const cleanedZip = zip.replace(/\D/g, ''); // Remove non-digits
    if (cleanedZip.length === 5) {
      const isInRange = serviceAreaZipCodes.includes(cleanedZip);
      setZipCodeResult(isInRange);
      return isInRange;
    }
    return null;
  }, []);

  const handleZipCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    setZipCode(value);
    if (value.length === 5) {
      checkZipCode(value);
    } else {
      setZipCodeResult(null);
    }
  };

  const handleZipCodeSubmit = () => {
    if (zipCode.length === 5) {
      checkZipCode(zipCode);
    }
  };

  const handleZipCodeKeyPress = (e) => {
    if (e.key === 'Enter' && zipCode.length === 5) {
      handleZipCodeSubmit();
    }
  };

  // Preload the hero image for better quality
  useEffect(() => {
    const img = new Image();
    img.src = '/HeroSection.png';
    img.onload = () => {
      setImageLoaded(true);
    };
  }, []);

  // Throttle mouse move handler with requestAnimationFrame
  const handleMouseMove = useCallback((e) => {
    if (!requestRef.current) {
      requestRef.current = requestAnimationFrame((timestamp) => {
        if (previousTimeRef.current !== undefined) {
          const { clientX, clientY } = e;
          const x = (clientX / window.innerWidth) * 100;
          const y = (clientY / window.innerHeight) * 100;
          setMousePosition({ x, y });
        }
        previousTimeRef.current = timestamp;
        requestRef.current = null;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [handleMouseMove]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      requestAnimationFrame(() => {
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      });
    }
  }, []);

  return (
    <section 
      className="hero hero-parallax" 
      id="home" 
      style={{
        '--mouse-x': `${mousePosition.x}%`, 
        '--mouse-y': `${mousePosition.y}%`
      }}
    >
      <div 
        className="hero-background"
        style={{
          backgroundImage: 'url(/HeroSection.png)',
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
      >
        <div className="layer layer-1"></div>
        <div className="layer layer-2"></div>
        <div className="layer layer-3"></div>
      </div>
      
      <div className="hero-overlay"></div>

      <div className="hero-content">
        <div className="hero-logo">
          <img 
            src="/UpdatedLogo2-6-26.png" 
            alt="Gardenia Landscaping Logo"
            width="400"
            height="auto"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <h1>Expert Lawn Care You Can Count On!</h1>
        
        <div className="hero-zip-checker">
          <p className="zip-checker-label">Check if we service your area:</p>
          <div className="zip-input-container">
            <input
              type="text"
              className="zip-input"
              placeholder="Enter ZIP Code"
              value={zipCode}
              onChange={handleZipCodeChange}
              onKeyPress={handleZipCodeKeyPress}
              maxLength={5}
              pattern="[0-9]*"
              inputMode="numeric"
            />
            {zipCodeResult !== null && (
              <div className={`zip-result ${zipCodeResult ? 'in-range' : 'out-of-range'}`}>
                {zipCodeResult ? (
                  <span>Great news! We service your area. Click the "Get Free Quote" button below!</span>
                ) : (
                  <span>We aren't in your neighborhood yet, but leave your email and we'll notify you when we expand!</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="hero-buttons">
          <button 
            onClick={() => scrollToSection('contact')} 
            className={`btn btn-primary ${zipCodeResult === true ? 'glow' : ''}`}
          >
            Get Free Quote
          </button>
          <button 
            onClick={() => scrollToSection('services')} 
            className="btn btn-secondary"
          >
            Our Services
          </button>
        </div>
      </div>
    </section>
  );
};

export default React.memo(Hero); 